// shared.jsx — primitives used across every screen.
// Dark by default; pass explicit overrides via the `style` prop.

export const SCREEN_W = 402;

// ──────────────────────────────────────────────────────────────
// Opal wordmark — real logotype (from assets/Opal Logo Black.svg)
// ──────────────────────────────────────────────────────────────
import { OpalWordmarkLogo } from './brand.jsx';
import { peekVariant } from './lib/experiments';

// ── Experiment: start_polish_v1 (PR #118 polish bundle, /2026-start) ──
// Scoped, exposure-free read of the design-polish A/B test. True only for
// users on the /2026-start funnel assigned to `treatment`. peekVariant()
// never fires $exposure — exposure is owned by the single <Experiment> wrap
// on the s0_hero CTA (screens-phase1.jsx), the first screen every 2026-start
// user sees. Every downstream surface (this button, the s8 loader, the s47
// email gate, the paywall recap) only READS the already-assigned variant, so
// the experiment denominator stays clean and other funnels stay out of it.
export function startPolishTreatment() {
  if (typeof window === 'undefined') return false;
  if (window.__opalFunnel?.slug !== '2026-start') return false;
  return peekVariant('start_polish_v1')?.value === 'treatment';
}

export function OpalWordmark({ height = 18, color = '#fff' }) {
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', height }}>
      <OpalWordmarkLogo height={height} color={color} />
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
// Primary button — lime→cyan gradient, matches Opal brand
// ──────────────────────────────────────────────────────────────
export function PrimaryButton({ children, onClick, disabled, style = {}, size = 'lg', accent }) {
  const pal = accent || (typeof window !== 'undefined' && window.__opalAccent) || 'gradient';
  const bg = pal === 'gradient'
    ? 'var(--opal-gradient-primary-h)'
    : pal === 'lime' ? 'var(--opal-lime)'
    : pal === 'cyan' ? 'var(--opal-cyan)'
    : pal === 'mint' ? 'var(--opal-mint)'
    : pal === 'pink' ? 'var(--opal-pink)'
    : 'var(--opal-gradient-primary-h)';
  const h = size === 'lg' ? 56 : size === 'md' ? 48 : 40;
  // start_polish_v1 treatment: press feedback on touch, not just mouse
  // (most traffic is mobile and got zero tactile response before).
  const touchFeedback = startPolishTreatment() ? {
    onTouchStart: (e) => !disabled && (e.currentTarget.style.transform = 'scale(0.98)'),
    onTouchEnd: (e) => (e.currentTarget.style.transform = ''),
    onTouchCancel: (e) => (e.currentTarget.style.transform = ''),
  } : null;
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        width: '100%',
        height: h,
        borderRadius: 9999,
        border: 'none',
        background: disabled ? 'rgba(255,255,255,0.12)' : bg,
        color: disabled ? 'rgba(255,255,255,0.35)' : '#000',
        fontFamily: 'var(--font-display)',
        fontWeight: 600,
        fontSize: 17,
        letterSpacing: '-0.01em',
        cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'transform 120ms var(--ease-spring), opacity 120ms',
        boxShadow: disabled ? 'none' : 'inset 0 1px 0 rgba(255,255,255,0.5), 0 10px 30px rgba(212,255,156,0.18)',
        ...style,
      }}
      onMouseDown={(e) => !disabled && (e.currentTarget.style.transform = 'scale(0.98)')}
      onMouseUp={(e) => (e.currentTarget.style.transform = '')}
      onMouseLeave={(e) => (e.currentTarget.style.transform = '')}
      {...touchFeedback}
    >{children}</button>
  );
}

// Ghost / secondary button
export function GhostButton({ children, onClick, style = {} }) {
  return (
    <button
      onClick={onClick}
      style={{
        width: '100%',
        height: 52,
        borderRadius: 9999,
        border: '1px solid var(--ios-hairline)',
        background: 'transparent',
        color: 'var(--fg1)',
        fontFamily: 'var(--font-display)',
        fontWeight: 500,
        fontSize: 16,
        cursor: 'pointer',
        ...style,
      }}
    >{children}</button>
  );
}

// ──────────────────────────────────────────────────────────────
// Top progress bar + back chevron (fixed to top of frame)
// `section` (optional) renders a small section-name pill above the bar so
// the user sees which phase of the quiz they're in (Paladin pattern).
// ──────────────────────────────────────────────────────────────
export function FunnelHeader({ progress, onBack, showBack = true, rightSlot, hideProgress = false, section }) {
  return (
    <div style={{
      position: 'sticky',
      top: 0,
      zIndex: 20,
      padding: section ? '10px 20px 12px' : '14px 20px 12px',
      background: 'linear-gradient(180deg, rgba(5,5,5,0.95) 0%, rgba(5,5,5,0.85) 70%, rgba(5,5,5,0) 100%)',
      backdropFilter: 'blur(12px)',
      WebkitBackdropFilter: 'blur(12px)',
    }}>
      {section && (
        <div style={{
          textAlign: 'center', marginBottom: 8,
          fontFamily: 'var(--font-display)',
          fontSize: 10, fontWeight: 600, letterSpacing: '0.16em',
          textTransform: 'uppercase',
          color: 'var(--opal-lime)',
        }}>{section}</div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        {showBack && onBack ? (
          <button onClick={onBack} aria-label="Back" type="button" style={{
            width: 36, height: 36, borderRadius: 9999,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'rgba(255,255,255,0.05)',
            border: '1px solid rgba(255,255,255,0.08)',
            cursor: 'pointer',
            color: 'var(--fg1)', padding: 0,
            flexShrink: 0,
          }}>
            <svg width="10" height="16" viewBox="0 0 10 16" fill="none" aria-hidden="true" focusable="false">
              <path d="M8 2L2 8l6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        ) : <div style={{ width: 36, flexShrink: 0 }} />}
        {hideProgress ? (
          <div style={{ flex: 1 }} />
        ) : (
          <div style={{
            flex: 1, height: 4, borderRadius: 9999,
            background: 'var(--ios-glass-10)',
            overflow: 'hidden',
          }}>
            <div style={{
              height: '100%',
              width: `${Math.max(0, Math.min(1, progress)) * 100}%`,
              background: 'var(--opal-gradient-primary-h)',
              borderRadius: 9999,
              transition: 'width 400ms var(--ease-out-soft)',
            }}/>
          </div>
        )}
        <div style={{ minWidth: 36, display: 'flex', justifyContent: 'flex-end', flexShrink: 0 }}>
          {rightSlot}
        </div>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
// Option tile (tap card with radio or checkbox)
// ──────────────────────────────────────────────────────────────
export function OptionTile({ label, sub, icon, selected, onClick, multi }) {
  const iconIsString = typeof icon === 'string';
  return (
    <button
      onClick={onClick}
      style={{
        width: '100%',
        display: 'flex', alignItems: 'center', gap: 14,
        padding: '16px 16px',
        borderRadius: 16,
        background: selected
          ? 'linear-gradient(180deg, rgba(212,255,156,0.12), rgba(158,249,255,0.06))'
          : 'var(--ios-glass-5)',
        border: selected ? '1.5px solid var(--opal-lime)' : '1px solid var(--ios-hairline)',
        color: 'var(--fg1)',
        fontFamily: 'var(--font-display)',
        textAlign: 'left',
        cursor: 'pointer',
        transition: 'all 180ms var(--ease-out-soft)',
        boxShadow: selected
          ? '0 0 0 4px rgba(212,255,156,0.08), inset 0 1px 0 rgba(255,255,255,0.06)'
          : 'inset 0 1px 0 rgba(255,255,255,0.04)',
      }}
    >
      {icon && (
        iconIsString ? (
          <div style={{
            width: 36, height: 36, borderRadius: 10,
            background: selected ? 'rgba(212,255,156,0.18)' : 'rgba(255,255,255,0.06)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 18, flexShrink: 0,
            border: '1px solid rgba(255,255,255,0.06)',
          }}>{icon}</div>
        ) : (
          <div style={{ flexShrink: 0 }}>{icon}</div>
        )
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 16, fontWeight: 600, lineHeight: '20px', letterSpacing: '-0.01em' }}>{label}</div>
        {sub && <div style={{ marginTop: 3, fontSize: 13, fontWeight: 400, color: 'var(--fg2)', lineHeight: '17px' }}>{sub}</div>}
      </div>
      <div style={{
        width: 22, height: 22,
        borderRadius: multi ? 6 : 9999,
        border: selected ? 'none' : '1.5px solid var(--fg3)',
        background: selected ? 'var(--opal-lime)' : 'transparent',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
      }}>
        {selected && (
          <svg width="12" height="12" viewBox="0 0 12 12">
            <path d="M2 6l3 3 5-6" stroke="#000" strokeWidth="2.2" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        )}
      </div>
    </button>
  );
}

// ──────────────────────────────────────────────────────────────
// Docked CTA footer — fixed to the bottom of the viewport/frame so the
// primary action remains visible even when screen content scrolls. The spacer
// preserves scroll room so final content is not hidden behind the dock.
// ──────────────────────────────────────────────────────────────
export function StickyFooter({ children, style = {}, reserveHeight = 118 }) {
  return (
    <>
      <div style={{
        position: 'fixed', left: '50%', bottom: 0, transform: 'translateX(-50%)', zIndex: 50,
        width: 'min(100vw, 600px)', maxWidth: '100%', boxSizing: 'border-box',
        padding: '14px 22px max(28px, env(safe-area-inset-bottom))',
        background: 'linear-gradient(180deg, rgba(5,5,5,0) 0%, rgba(5,5,5,0.88) 40%, rgba(5,5,5,0.98) 100%)',
        ...style,
      }}>
        {children}
      </div>
      <div aria-hidden="true" style={{ height: reserveHeight, flexShrink: 0 }} />
    </>
  );
}

// ──────────────────────────────────────────────────────────────
// Screen scaffold — flows in the funnel-frame scroll container.
// Min-height fills viewport; content flexes so CTA sits at bottom
// on short content but pushes down naturally on long content.
// ──────────────────────────────────────────────────────────────
export function Screen({ progress, onBack, onNext, ctaLabel = 'Continue', ctaDisabled, eyebrow, title, subtitle, children, hideCTA, footerSlot, headerRight, bg, section }) {
  return (
    <div style={{
      position: 'relative',
      minHeight: '100dvh',
      display: 'flex', flexDirection: 'column',
      background: bg || 'transparent',
    }}>
      <FunnelHeader progress={progress} onBack={onBack} showBack={!!onBack} rightSlot={headerRight} section={section} />
      <div style={{ flex: 1, padding: '18px 24px 16px', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        {eyebrow && <div className="eyebrow" style={{
          color: 'var(--opal-lime)', marginBottom: 12,
          fontSize: 11, letterSpacing: '0.14em', fontWeight: 600,
        }}>{eyebrow}</div>}
        {title && <h1 style={{
          margin: 0, fontFamily: 'var(--font-display)',
          fontWeight: 600, fontSize: 34, lineHeight: '38px',
          letterSpacing: '-0.025em', color: 'var(--fg1)',
          textWrap: 'balance',
        }}>{title}</h1>}
        {subtitle && <p style={{
          marginTop: 14, marginBottom: 0,
          fontSize: 15, lineHeight: '22px', color: 'var(--fg2)',
          fontFamily: 'var(--font-text)',
          textWrap: 'pretty',
        }}>{subtitle}</p>}
        <div style={{ marginTop: title ? 26 : 4, flex: 1 }}>
          {children}
        </div>
      </div>
      {!hideCTA && (
        <StickyFooter>
          {footerSlot}
          <PrimaryButton onClick={onNext} disabled={ctaDisabled}>{ctaLabel}</PrimaryButton>
        </StickyFooter>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
// Slider (0-indexed range w/ value bubble)
// ──────────────────────────────────────────────────────────────
export function BigSlider({ value, min, max, step = 1, onChange, valueFormatter, label = 'hours on phone per day', minLabel, maxLabel }) {
  const pct = (value - min) / (max - min);
  return (
    <div style={{ padding: '8px 4px 0' }}>
      <div style={{
        textAlign: 'center',
        fontFamily: 'var(--font-display)',
        fontSize: 72, fontWeight: 600, lineHeight: 1,
        letterSpacing: '-0.03em',
        background: 'var(--opal-gradient-primary-h)',
        WebkitBackgroundClip: 'text', backgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
      }}>{valueFormatter ? valueFormatter(value) : value}</div>
      <div style={{ textAlign: 'center', color: 'var(--fg2)', fontSize: 15, marginTop: 8 }}>
        {label}
      </div>
      <div style={{ position: 'relative', marginTop: 36, padding: '0 4px' }}>
        <div style={{
          position: 'absolute', left: 4, right: 4, top: '50%',
          height: 6, borderRadius: 9999, background: 'var(--ios-glass-10)', transform: 'translateY(-50%)',
        }} />
        <div style={{
          position: 'absolute', left: 4, top: '50%',
          height: 6, width: `calc(${pct * 100}% )`, borderRadius: 9999,
          background: 'var(--opal-gradient-primary-h)', transform: 'translateY(-50%)',
        }} />
        <input
          type="range" min={min} max={max} step={step} value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          style={{
            width: '100%',
            appearance: 'none', WebkitAppearance: 'none',
            background: 'transparent',
            position: 'relative', zIndex: 2,
            height: 28,
          }}
          className="opal-range"
        />
      </div>
      <div style={{
        display: 'flex', justifyContent: 'space-between',
        color: 'var(--fg3)', fontSize: 12, marginTop: 8,
      }}>
        <span>{minLabel !== undefined ? minLabel : `${min}h`}</span>
        <span>{maxLabel !== undefined ? maxLabel : `${max}+h`}</span>
      </div>
    </div>
  );
}

// Global style for range thumb — runs once on module import.
if (typeof document !== 'undefined' && !document.getElementById('opal-range-css')) {
  const s = document.createElement('style');
  s.id = 'opal-range-css';
  s.textContent = `
    .opal-range::-webkit-slider-thumb {
      -webkit-appearance: none; appearance: none;
      width: 28px; height: 28px; border-radius: 9999px;
      background: #fff; cursor: pointer; border: none;
      box-shadow: 0 2px 8px rgba(0,0,0,0.4), 0 0 0 4px rgba(212,255,156,0.25);
    }
    .opal-range::-moz-range-thumb {
      width: 28px; height: 28px; border-radius: 9999px;
      background: #fff; cursor: pointer; border: none;
      box-shadow: 0 2px 8px rgba(0,0,0,0.4);
    }
  `;
  document.head.appendChild(s);
}

// ──────────────────────────────────────────────────────────────
// Small atoms
// ──────────────────────────────────────────────────────────────
export function Chip({ children, color = 'lime' }) {
  const bg = color === 'lime' ? 'rgba(212,255,156,0.14)' :
             color === 'cyan' ? 'rgba(158,249,255,0.14)' :
             color === 'pink' ? 'rgba(255,87,117,0.14)' :
             'rgba(255,255,255,0.08)';
  const fg = color === 'lime' ? 'var(--opal-lime)' :
             color === 'cyan' ? 'var(--opal-cyan)' :
             color === 'pink' ? 'var(--opal-pink)' :
             'var(--fg2)';
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: '6px 10px', borderRadius: 9999,
      background: bg, color: fg,
      fontFamily: 'var(--font-display)', fontWeight: 600,
      fontSize: 11, letterSpacing: '0.04em', textTransform: 'uppercase',
    }}>{children}</span>
  );
}

export function GemIcon({ size = 28, color = 'var(--opal-lime)' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none">
      <defs>
        <linearGradient id="gemg" x1="0" x2="1" y1="0" y2="1">
          <stop offset="0" stopColor="rgb(212,255,156)" />
          <stop offset="1" stopColor="rgb(158,249,255)" />
        </linearGradient>
      </defs>
      <path d="M8 4h16l6 8-14 16L2 12z" fill="url(#gemg)" opacity="0.95"/>
      <path d="M8 4l8 8 8-8M2 12h28M16 12v16" stroke="rgba(0,0,0,0.25)" strokeWidth="1" fill="none"/>
    </svg>
  );
}

// Star row
export function Stars({ count = 5, size = 12 }) {
  return (
    <div style={{ display: 'inline-flex', gap: 2 }}>
      {Array.from({ length: count }).map((_, i) => (
        <svg key={i} width={size} height={size} viewBox="0 0 12 12">
          <path d="M6 .5l1.6 3.6 3.9.4-2.9 2.7.8 3.9L6 9.3l-3.4 1.8.8-3.9L.5 4.5l3.9-.4z" fill="var(--opal-yellow)"/>
        </svg>
      ))}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
// Age-conditional content swap. Keeps 45+ tailoring centralized so screens
// stay declarative — `pickByAge(state.age, { default: a, '45+': b })`.
// Falls back to `default` when no matching key.
//
// The 45+ split (45-54 / 55-65 or 55-64 / 65+) means a single '45+' key should
// cover all the subdivision buckets without forcing every call site to
// enumerate. If the caller wants to target a specific bracket they can pass
// that key explicitly; otherwise the '45+' entry applies as a fall-through.
// (/detox uses 55-65; /2026-start uses 55-64 — both are treated as 45+.)
// ──────────────────────────────────────────────────────────────
const AGES_45_PLUS = new Set(['45-54', '55-65', '55-64', '65+', '45+']);

export function isOver45(age) {
  return AGES_45_PLUS.has(age);
}

export function pickByAge(age, options) {
  if (!options) return undefined;
  if (options[age] !== undefined) return options[age];
  // Fall-through: any 45+ subdivision matches a '45+' override.
  if (isOver45(age) && options['45+'] !== undefined) return options['45+'];
  return options.default;
}

// ──────────────────────────────────────────────────────────────
// Scroll-window definitions — used by s7b time-of-day capture and the
// DayTimeline visual on s48b. Hours are 24-hour clock for the timeline.
// ──────────────────────────────────────────────────────────────
export const SCROLL_WINDOWS = {
  morning:    { id: 'morning',    label: 'Early morning', range: '6–10 am',     startHour: 6,  endHour: 10 },
  midday:     { id: 'midday',     label: 'Mid-day',       range: '10 am–2 pm',  startHour: 10, endHour: 14 },
  afternoon:  { id: 'afternoon',  label: 'Afternoon',     range: '2–6 pm',      startHour: 14, endHour: 18 },
  evening:    { id: 'evening',    label: 'Evening',       range: '6–10 pm',     startHour: 18, endHour: 22 },
  late_night: { id: 'late_night', label: 'Late night',    range: 'after 10 pm', startHour: 22, endHour: 30 }, // crosses midnight, capped at 6am
};

export function formatScrollWindows(ids) {
  if (!ids || !ids.length) return '—';
  return ids.map((id) => SCROLL_WINDOWS[id]?.label).filter(Boolean).join(', ');
}

// ──────────────────────────────────────────────────────────────
// DayTimeline — horizontal 24-hour strip with locked blocks during each
// selected scroll window. Used on s48b plan sneak peek.
// ──────────────────────────────────────────────────────────────
export function DayTimeline({ scrollWindows = [], heavyApps = [], height = 88 }) {
  const HOURS = 24;
  // Build occupied ranges; the late_night window crosses midnight, so we clamp
  // its tail to 06:00 the next morning (rendering twice).
  const ranges = [];
  for (const id of scrollWindows) {
    const w = SCROLL_WINDOWS[id];
    if (!w) continue;
    if (w.endHour <= HOURS) {
      ranges.push({ start: w.startHour, end: w.endHour, id });
    } else {
      ranges.push({ start: w.startHour, end: HOURS, id });
      ranges.push({ start: 0, end: w.endHour - HOURS, id });
    }
  }

  const appCount = heavyApps.length;
  const labelHours = [0, 6, 12, 18, 24];

  return (
    <div style={{
      padding: 14,
      borderRadius: 18,
      background: 'linear-gradient(180deg, rgba(212,255,156,0.06), rgba(158,249,255,0.04))',
      border: '1px solid rgba(212,255,156,0.18)',
    }}>
      <div style={{
        position: 'relative', height,
        borderRadius: 12,
        background: 'rgba(255,255,255,0.04)',
        border: '1px solid rgba(255,255,255,0.08)',
        overflow: 'hidden',
      }}>
        {/* hour gridlines */}
        {[6, 12, 18].map((h) => (
          <div key={h} style={{
            position: 'absolute', top: 0, bottom: 0,
            left: `${(h / HOURS) * 100}%`,
            width: 1,
            background: 'rgba(255,255,255,0.06)',
          }} />
        ))}
        {/* locked blocks */}
        {ranges.map((r, i) => {
          const left = (r.start / HOURS) * 100;
          const width = ((r.end - r.start) / HOURS) * 100;
          return (
            <div key={i} style={{
              position: 'absolute', top: 6, bottom: 6,
              left: `${left}%`, width: `${width}%`,
              borderRadius: 8,
              background: 'linear-gradient(180deg, rgba(255,87,117,0.45), rgba(255,87,117,0.20))',
              border: '1px solid rgba(255,87,117,0.55)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              gap: 4,
              boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.08)',
            }}>
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                <path d="M3 5V3.5a3 3 0 016 0V5m-7 0h8v5a1 1 0 01-1 1H3a1 1 0 01-1-1V5z"
                  stroke="#fff" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              {appCount > 0 && width > 8 && (
                <span style={{
                  fontFamily: 'var(--font-display)',
                  fontSize: 10, fontWeight: 600, color: '#fff',
                  letterSpacing: '0.02em',
                }}>{appCount} {appCount === 1 ? 'app' : 'apps'}</span>
              )}
            </div>
          );
        })}
      </div>
      {/* hour labels */}
      <div style={{
        position: 'relative', height: 18, marginTop: 6,
        color: 'var(--fg3)', fontFamily: 'var(--font-display)',
        fontSize: 10, letterSpacing: '0.04em',
      }}>
        {labelHours.map((h) => {
          const label = h === 0 ? '12a' : h === 12 ? '12p' : h === 24 ? '12a' : h < 12 ? `${h}a` : `${h - 12}p`;
          const left = (h / HOURS) * 100;
          return (
            <span key={h} style={{
              position: 'absolute', top: 0, left: `${left}%`,
              transform: h === 0 ? 'translateX(0)' : h === 24 ? 'translateX(-100%)' : 'translateX(-50%)',
            }}>{label}</span>
          );
        })}
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
// ReflectionCard — small icon + label + value tile. Used on acknowledgement
// screens (s10b, s20b, s40b) and the pre-plan summary (s45b).
// ──────────────────────────────────────────────────────────────
export function ReflectionCard({ icon, label, value, accent = 'lime' }) {
  const ringColor = accent === 'lime' ? 'rgba(212,255,156,0.30)'
                  : accent === 'cyan' ? 'rgba(158,249,255,0.30)'
                  : 'rgba(255,255,255,0.10)';
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 12,
      padding: '12px 14px',
      borderRadius: 14,
      background: 'var(--ios-glass-5)',
      border: '1px solid var(--ios-hairline)',
      boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.04)',
    }}>
      {icon && (
        <div style={{
          width: 36, height: 36, borderRadius: 10,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'rgba(255,255,255,0.05)',
          border: `1px solid ${ringColor}`,
          fontSize: 18, flexShrink: 0,
        }}>{icon}</div>
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          color: 'var(--fg3)',
          fontFamily: 'var(--font-display)',
          fontSize: 11, fontWeight: 600, letterSpacing: '0.06em',
          textTransform: 'uppercase',
        }}>{label}</div>
        <div style={{
          marginTop: 2,
          fontFamily: 'var(--font-display)',
          fontSize: 16, fontWeight: 600, color: 'var(--fg1)',
          // Default 0 letter-spacing so commas in list values ("YouTube, TikTok,
          // Instagram") get natural whitespace. The display font's tightened
          // tracking compressed the post-comma gap enough that values read as
          // one run-on token.
          letterSpacing: 0,
          lineHeight: '20px',
          textWrap: 'pretty',
        }}>{value}</div>
      </div>
    </div>
  );
}

// press-able card (selectable bool tile)
export function BoolTile({ selected, onClick, children, style = {} }) {
  return (
    <button onClick={onClick} style={{
      flex: 1,
      padding: '18px 12px',
      borderRadius: 18,
      background: selected ? 'rgba(212,255,156,0.10)' : 'var(--ios-glass-5)',
      border: selected ? '1.5px solid var(--opal-lime)' : '1px solid var(--ios-hairline)',
      color: 'var(--fg1)', fontFamily: 'var(--font-display)',
      fontSize: 17, fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 180ms var(--ease-out-soft)',
      ...style,
    }}>{children}</button>
  );
}
